import {
    getPrecisePriceWithCurrency,
    getPriceWithDisplayCurrencyByPrecision,
    getFreelancerExchangeRate,
} from 'arbitrip-common/general/utils/Currencies';
import { Tooltip } from '@mui/material';
import { isAccountBooking } from '@/utils/AccountBankTransfer';

// OB-19 — the server records an Account request at `payment.sum` / `payment.currency`, so that is the
// Account total; the deal-derived figure can differ from it.
function getAccountTotalToPay(booking) {
    if (!isAccountBooking(booking)) {
        return null;
    }
    return { total_to_pay: booking?.payment?.sum, currency: booking?.payment?.currency };
}

export function getBookingPricingBreakdown(booking) {
    const total_net_price = booking?.deal?.cheapopoPricing?.purchase_price;
    const total_client_price = booking?.deal?.cheapopoPricing?.cheapopoTotalPrice;

    const total_commission =
        booking?.deal?.cheapopoPricing?.agentPricing?.absolute_margin_total ||
        booking?.deal?.cheapopoPricing?.commissionTotal ||
        0;

    const currency = booking?.deal?.cheapopoPricing?.currency;
    const points_in_currency = booking?.payment?.loyalty?.points_in_currency || 0;

    const netPlusCommission =
        Number.isFinite(total_net_price) && Number.isFinite(total_commission)
            ? total_net_price + total_commission
            : undefined;

    const baseTotal = Number.isFinite(netPlusCommission)
        ? netPlusCommission
        : Number.isFinite(total_client_price)
            ? total_client_price
            : undefined;

    const accountTotal = getAccountTotalToPay(booking);

    const total_to_pay = accountTotal
        ? Number.isFinite(accountTotal.total_to_pay)
            ? accountTotal.total_to_pay
            : NaN
        : Number.isFinite(baseTotal)
            ? baseTotal - points_in_currency
            : NaN;

    const total_to_pay_currency = accountTotal ? accountTotal.currency : currency;

    const isPriceable = Number.isFinite(total_to_pay) && !!total_to_pay_currency;

    return {
        total_net_price,
        total_client_price,
        total_commission,
        currency,
        points_in_currency,
        total_to_pay,
        total_to_pay_currency,
        isPriceable,
    };
}

export function getBookingTotalToPay(booking) {
    const { total_to_pay, total_to_pay_currency } = getBookingPricingBreakdown(booking);
    return { total_to_pay, currency: total_to_pay_currency };
}

function createCurrencyTotals(currency) {
    return {
        currency,
        total_net_price: 0,
        total_client_price: 0,
        total_commission: 0,
        points_in_currency: 0,
        total_to_pay: 0,
    };
}

export function getAggregatedPricingBreakdown(bookings = []) {
    const totalsByCurrency = {};
    const unpriceableBookings = [];

    bookings.forEach((booking) => {
        const breakdown = getBookingPricingBreakdown(booking);
        const { currency, isPriceable } = breakdown;

        if (!isPriceable) {
            unpriceableBookings.push(booking);
            return;
        }

        const rowsCurrency = currency || breakdown.total_to_pay_currency;

        if (!totalsByCurrency[rowsCurrency]) {
            totalsByCurrency[rowsCurrency] = createCurrencyTotals(rowsCurrency);
        }
        if (!totalsByCurrency[breakdown.total_to_pay_currency]) {
            totalsByCurrency[breakdown.total_to_pay_currency] = createCurrencyTotals(breakdown.total_to_pay_currency);
        }

        const acc = totalsByCurrency[rowsCurrency];

        acc.total_net_price += Number.isFinite(breakdown.total_net_price) ? breakdown.total_net_price : 0;
        acc.total_client_price += Number.isFinite(breakdown.total_client_price) ? breakdown.total_client_price : 0;
        acc.total_commission += Number.isFinite(breakdown.total_commission) ? breakdown.total_commission : 0;
        acc.points_in_currency += Number.isFinite(breakdown.points_in_currency) ? breakdown.points_in_currency : 0;
        totalsByCurrency[breakdown.total_to_pay_currency].total_to_pay += breakdown.total_to_pay;
    });

    const currencies = Object.keys(totalsByCurrency);

    return {
        currencies,
        totalsByCurrency,
        isMixedCurrency: currencies.length > 1,
        unpriceableBookings,
    };
}

export function formatAmountsByCurrency(totalsByCurrency, currencies, field) {
    return currencies
        .map((currency) => getPrecisePriceWithCurrency(totalsByCurrency[currency][field], currency))
        .join(' + ');
}

export function getAggregatedCommissionPercentageText(totalsByCurrency, currencies) {
    return currencies
        .map((currency) => {
            const { total_commission, total_client_price } = totalsByCurrency[currency];
            const percentage = total_client_price > 0 ? (total_commission / total_client_price) * 100 : 0;
            return `${percentage.toFixed(2)}%`;
        })
        .join(' + ');
}

export function getPriceValue(priceValue, currency, chargeCurrency, profile) {
    let title;
    if (
        priceValue > 0 &&
        chargeCurrency &&
        profile?.freelance_agent_pay_later_exchange_rates &&
        chargeCurrency !== currency
    ) {
        title = `approx. ${getPriceWithDisplayCurrencyByPrecision(priceValue, getFreelancerExchangeRate(profile, currency, chargeCurrency), chargeCurrency, 2)}`;
    }

    return (
        <Tooltip title={title} placement="left">
            <div className="price-value">{priceValue > 0 ? getPrecisePriceWithCurrency(priceValue, currency) : 0}</div>
        </Tooltip>
    );
}
